import { Link } from "react-router-dom";
import { Clock, ArrowRight, User } from "lucide-react";
import { BLOG_POSTS } from "@/data/siteData";
import { Badge } from "@/components/ui/badge";
import { CTABanner } from "@/components/CTABanner";

export default function BlogPage() {
  return (
    <div data-testid="blog-page">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3 animate-fade-in-up">
            Insights
          </p>
          <h1
            data-testid="blog-heading"
            className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-4 animate-fade-in-up animation-delay-100"
          >
            The Coffice Blog
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto font-body leading-relaxed animate-fade-in-up animation-delay-200">
            Tips, insights, and stories from the world of coworking, productivity,
            and the Pune startup ecosystem.
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            {BLOG_POSTS.map((post) => (
              <article
                key={post.id}
                data-testid={`blog-card-${post.slug}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] group"
              >
                <div className="aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Badge
                      variant="secondary"
                      className="bg-sky-50 text-sky-600 border-0 text-xs"
                    >
                      {post.category}
                    </Badge>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 group-hover:text-orange-500 transition-colors">
                    <Link to={`/blog/${post.slug}`} data-testid={`blog-title-${post.slug}`}>
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {post.author}
                    </span>
                    <Link
                      to={`/blog/${post.slug}`}
                      data-testid={`blog-read-${post.slug}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
                    >
                      Read
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Stay in the Loop"
        subtitle="Visit us to experience the Coffice difference first-hand."
        buttonText="Book a Visit"
        buttonLink="/contact"
      />
    </div>
  );
}
